import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { from, Observable, switchMap } from 'rxjs';
import { RestfulEndpoint } from './restful-endpoint.service';
import { AuthService } from '../core/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { NotificationsService } from '../layout/common/notifications/notifications.service';
import { GlobalService } from './global.service';
import { UserService } from 'app/core/user/user.service';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  constructor(
    private afMessaging: AngularFireMessaging,
    private endPoint: RestfulEndpoint,
    private _auth: AuthService,
    private _toaster: ToastrService,
    private _transloco: TranslocoService,
    private _notifications: NotificationsService,
    private _globalService: GlobalService,
    private userService: UserService,
  ) {
    if (this._globalService.isNotificationsSupported()) {
      // Listen for messages from the service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NEW_NOTIFICATION') {
          this.handleBackgroundNotification(event.data.payload);
        }
      });
    }
  }

  updateFCMToken(body: { notification_token: string; device_id: string }): Observable<any> {
    return from(this.userService.getUserId()).pipe(
      switchMap((userId) => {
        return this.endPoint.post(`v2/users/${userId}/update-device-token`, body);
      }),
    );
  }

  generateDeviceId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  requestPermission() {
    if (this._globalService.isNotificationsSupported()) {
      this.getTokenWithRetry();
    }
  }

  getTokenWithRetry(retries = 3, delay = 1000) {
    this.afMessaging.requestPermission.subscribe({
      next: () => {
        this.afMessaging.requestToken.subscribe({
          next: (token) => {
            if (token) {
              const deviceId = localStorage.getItem('deviceId');

              if (!deviceId) {
                // No deviceId found, delete the FCM token from Firebase and localStorage
                this.afMessaging.getToken.subscribe({
                  next: (currentToken) => {
                    if (currentToken) {
                      // Delete the token from Firebase's cache
                      this.afMessaging.deleteToken(currentToken).subscribe({
                        next: () => {
                          // console.log('FCM token deleted from Firebase.');

                          // Clear the old token and deviceId from localStorage
                          localStorage.removeItem('fcm_token');
                          localStorage.removeItem('deviceId');

                          // Request a new token after deletion
                          this.afMessaging.getToken.subscribe({
                            next: (newToken) => {
                              if (newToken) {
                                // console.log('New FCM Token:', newToken);

                                // Save the new token and device ID in localStorage
                                localStorage.setItem('fcm_token', newToken);
                                const newDeviceId = this.getOrCreateDeviceId(); // Create or get a new device ID
                                localStorage.setItem('deviceId', newDeviceId);

                                // Update the backend with the new token and device ID
                                this.updateFCMToken({
                                  notification_token: newToken,
                                  device_id: newDeviceId,
                                }).subscribe({
                                  next: () => {
                                    // console.log('FCM token and device ID updated successfully.');
                                  },
                                  error: (error) => {
                                    if (error.error?.errors?.length > 0) {
                                      error.error.errors.forEach((err) => {
                                        this._toaster.error(err);
                                      });
                                    } else {
                                      this._toaster.error(
                                        this._transloco.translate(
                                          'Something went wrong, please try again.',
                                        ),
                                      );
                                    }
                                  },
                                });
                              }
                            },
                            error: (error) => {
                              console.error(
                                'Error requesting new FCM token after deletion:',
                                error,
                              );
                            },
                          });
                        },
                        error: (error) => {
                          console.error('Failed to delete FCM token from Firebase:', error);
                        },
                      });
                    }
                  },
                  error: (error) => {
                    console.error('Error retrieving current FCM token:', error);
                  },
                });
              } else {
                // console.log('Existing deviceId:', deviceId);
                // console.log('Existing FCM token:', localStorage.getItem('fcm_token'));

                // Update FCM token and device ID in localStorage without deletion
                localStorage.setItem('fcm_token', token);

                // Update the backend with the new token and existing device ID
                this.updateFCMToken({
                  notification_token: token,
                  device_id: deviceId,
                }).subscribe({
                  next: () => {
                    // console.log('FCM token updated successfully.');
                  },
                  error: (error) => {
                    if (error.error?.errors?.length > 0) {
                      error.error.errors.forEach((err) => {
                        this._toaster.error(err);
                      });
                    } else {
                      this._toaster.error(
                        this._transloco.translate('Something went wrong, please try again.'),
                      );
                    }
                  },
                });
              }
            } else if (retries > 0) {
              setTimeout(() => this.getTokenWithRetry(retries - 1, delay), delay);
            }
          },
          error: (error) => {
            console.error('Error requesting FCM token:', error);
            if (retries > 0) {
              setTimeout(() => this.getTokenWithRetry(retries - 1, delay), delay);
            }
          },
        });
      },
      error: (error) => {
        console.error('Permission denied for notifications:', error);
      },
    });
  }

  listen() {
    this.afMessaging.messages.subscribe((message) => {
      this._toaster.info(message.notification.body, message.notification.title);
      this.fetchNotifications();
    });
  }

  private fetchNotifications() {
    this._notifications.getNotifications().subscribe({
      next: () => {},
      error: (error) => {
        this._toaster.error(
          error.message || this._transloco.translate('Something went wrong, please try again.'),
        );
      },
    });
  }

  private handleBackgroundNotification(notification) {
    this._toaster.info(notification.body, notification.title);
    this.fetchNotifications();
  }
}
